import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  SaveErrorText,
} from "./analytics.pane.styles";

export {
  EmptyNote,
  FormField,
  FormLabel,
  FormRow,
} from "./analytics.form.styles";

export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  gap: 16px;
`;

export const ModeToggleRow = styled.div`
  display: flex;
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 10px;
  overflow: hidden;
`;

export const ModeBtn = styled.button<{ $active: boolean }>`
  ${TypographyV2.s1}
  flex: 1;
  padding: 7px 12px;
  border: none;
  cursor: pointer;
  background: ${({ $active }) =>
    $active ? ColorV2.ElectricBlue : ColorV2.White};
  color: ${({ $active }) => ($active ? ColorV2.White : ColorV2.Blue3)};
  transition:
    background 0.15s,
    color 0.15s;

  &:not(:last-child) {
    border-right: 1.5px solid ${ColorV2.Blue5};
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? ColorV2.ElectricBlue : ColorV2.Blue6};
  }
`;

export const ModeHint = styled.p`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const DropZone = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 20px;
  border: 1.5px dashed
    ${({ $active }) => ($active ? ColorV2.ElectricBlue : ColorV2.Blue5)};
  border-radius: 12px;
  background: ${({ $active }) => ($active ? ColorV2.Blue7 : ColorV2.White)};
  transition:
    border-color 0.15s,
    background 0.15s;
`;

export const DropZoneHint = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1.5px solid ${ColorV2.Blue6};
  border-radius: 10px;
  background: ${ColorV2.White};
`;

export const FileName = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChangeFileBtn = styled.button`
  ${TypographyV2.s1}
  color: ${ColorV2.ElectricBlue};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const ColumnSelect = styled.select`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  background: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 10px;
  padding: 8px 12px;
  appearance: none;
  cursor: pointer;
  width: 100%;
  outline: none;

  &:focus {
    border-color: ${ColorV2.ElectricBlue};
  }

  &:hover {
    border-color: ${ColorV2.Blue4};
  }
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SummaryBadge = styled.span<{ $tone: "positive" | "negative" }>`
  ${TypographyV2.s1}
  color: ${({ $tone }) =>
    $tone === "positive" ? ColorV2.PositiveGreen : ColorV2.NegativeRed};
`;

export const PreviewTableWrapper = styled.div`
  border: 1.5px solid ${ColorV2.Blue6};
  border-radius: 10px;
  overflow: hidden;
`;

export const PreviewTableScroll = styled.div`
  max-height: 260px;
  overflow-y: auto;
  overflow-x: auto;
`;

export const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const PreviewTableHead = styled.thead`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  position: sticky;
  top: 0;
  background: ${ColorV2.Blue7};

  th {
    text-align: left;
    padding: 8px 12px;
    font-weight: 500;
  }
`;

export const PreviewTableRow = styled.tr<{ $hasError: boolean }>`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  background: ${({ $hasError }) =>
    $hasError ? ColorV2.NegativeRedLight : ColorV2.White};

  &:not(:last-child) {
    border-bottom: 1px solid ${ColorV2.Blue6};
  }

  td {
    padding: 6px 12px;
    white-space: nowrap;
  }
`;

export const PreviewRowError = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.NegativeRed};
`;

// No dedicated pale-amber token exists in ColorV2 for a warning banner, so
// the background reuses UnplannedAbsenceYellow (the closest warning-toned
// token) at low opacity rather than introducing a standalone hex value.
export const WarningBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1.5px solid ${ColorV2.UnplannedAbsenceYellow};
  background: ${ColorV2.UnplannedAbsenceYellow}4d;
`;

export const WarningBannerTitle = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue1};
  font-weight: 500;
`;

export const WarningList = styled.ul`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue3};
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Capped so a large batch of conflicting dates can't push the preview
     table and Import button out of easy reach — this list scrolls on its
     own instead of dominating the pane's shared scroll area. */
  max-height: 120px;
  overflow-y: auto;
`;
